/**
 * GBP formatting — the single source of truth for turning money into text.
 *
 * ⚠ THERE ARE TWO AUDIENCES AND THEY NEED DIFFERENT STRINGS. Conflating them is
 * the trap this module exists to close, and it is a money bug, not a cosmetic
 * one:
 *
 *   HUMAN    `formatPrice(123450)`            -> "£1,234.50"
 *   MACHINE  `machineAmountGbpFromPence(...)` -> "1234.50 GBP"
 *
 * `Intl` emits a currency SYMBOL and a THOUSANDS SEPARATOR. Google Merchant
 * Center, Meta and Pinterest parse `price` as `<decimal> <ISO-4217>`, so feeding
 * them "£1,234.50" gets the product DISAPPROVED. The same applies to schema.org
 * `MonetaryAmount.value`, the TikTok export and every CSV: a spreadsheet or a
 * crawler wants a bare decimal, never a localised one.
 *
 * Note the two are IDENTICAL below £1,000 — which is exactly why this is worth a
 * guard rather than a code review. Every realistic test fixture passes under
 * either formatter; only a four-figure order reveals the difference, and by then
 * it is a live feed rejection.
 *
 * ── UNITS ARE IN THE NAME, ALWAYS ────────────────────────────────────────────
 * Money is stored as integer PENCE everywhere (`totalPence`), because floats
 * cannot represent currency exactly. A few config values are parsed as POUNDS
 * floats (`DEFAULT_SHIPPING_GBP`, `claim.ratePounds`). Both are supported, and
 * every function says which it takes — a `FromPence` helper handed pounds is a
 * 100x error, so the unit may never be implicit.
 *
 * Guarded by tests/unit/lib/money-formatting.spec.ts (MONEY-1..n), which pins
 * the machine forms against the exact strings the feeds emitted before this
 * module existed, and asserts no machine surface reaches for `Intl`.
 */

/** Pence -> pounds, as a number. Integer pence in, exact 2dp value out. */
function toPounds(pence: number): number {
  return pence / 100;
}

/**
 * HUMAN-facing GBP, localised: `123450` -> `"£1,234.50"`.
 *
 * Use for the storefront, the admin, emails and push bodies — anywhere a person
 * reads it. Never for a feed, a CSV, or JSON-LD.
 */
export function formatPrice(pence: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(toPounds(pence));
}

/**
 * HUMAN-facing GBP that drops a `.00`: `1200` -> `"£12"`, `1250` -> `"£12.50"`.
 *
 * For conversational and marketing copy (the chat bot's quotes, referral reward
 * lines) where "£12.00" reads stiffly. Still localised, so £1,000+ keeps its
 * separator.
 */
export function formatPriceCompact(pence: number): string {
  const whole = pence % 100 === 0;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(toPounds(pence));
}

/**
 * MACHINE-readable bare decimal from PENCE: `123450` -> `"1234.50"`.
 *
 * No symbol, no separator, always 2dp. This is the CSV / spreadsheet / JSON-LD
 * `value` form.
 */
export function machineAmountFromPence(pence: number): string {
  return toPounds(pence).toFixed(2);
}

/**
 * MACHINE-readable bare decimal from POUNDS: `1234.5` -> `"1234.50"`.
 *
 * Only for values that are ALREADY pounds floats (parsed config, resolved
 * shipping rates). If you hold pence, use `machineAmountFromPence`.
 */
export function machineAmountFromPounds(pounds: number): string {
  return pounds.toFixed(2);
}

/**
 * MACHINE-readable with ISO-4217 code from PENCE: `123450` -> `"1234.50 GBP"`.
 *
 * The Google / Meta / Pinterest product-feed `price` and `sale_price` form.
 */
export function machineAmountGbpFromPence(pence: number): string {
  return `${machineAmountFromPence(pence)} GBP`;
}

/**
 * MACHINE-readable with ISO-4217 code from POUNDS: `1234.5` -> `"1234.50 GBP"`.
 *
 * The feed `shipping.price` form, whose rate is resolved as a pounds float.
 */
export function machineAmountGbpFromPounds(pounds: number): string {
  return `${machineAmountFromPounds(pounds)} GBP`;
}
